import { AuthShell } from '@/components/auth-shell'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Mail01Icon,
  ArrowLeft01Icon,
  CheckmarkCircle02Icon,
} from '@hugeicons/core-free-icons'
import toast from 'react-hot-toast'

import { authClient } from '@/lib/auth-client'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'

interface FormValues {
  email: string
}

export default function ForgotPassword() {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)

  const form = useForm<FormValues>({ defaultValues: { email: '' } })

  const onSubmit = async (data: FormValues) => {
    // Build the absolute URL the email enlace should land on. Better Auth will
    // append `?token=...` itself.
    const redirectTo = `${window.location.origin}/reset-password`

    const { error } = await authClient.requestPasswordReset({
      email: data.email,
      redirectTo,
    })

    if (error) {
      toast.error(
        error.message || 'No se pudo enviar el correo de recuperación',
      )
      return
    }

    setSubmittedEmail(data.email)
  }

  return (
    <AuthShell>
      <Card variant="plain">
        {submittedEmail ? (
          <>
            <CardHeader>
              <div className="mb-2 flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <HugeiconsIcon
                  icon={CheckmarkCircle02Icon}
                  className="size-6"
                />
              </div>
              <CardTitle variant="page">
                <h1>Revisa tu correo</h1>
              </CardTitle>
              <CardDescription>
                Si{' '}
                <span className="font-medium text-foreground">
                  {submittedEmail}
                </span>{' '}
                tiene una cuenta, te enviamos un enlace para restablecer la
                contraseña.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <p className="text-center text-sm text-muted-foreground">
                Si no llega, revisa la carpeta de spam. Por seguridad, el enlace
                tiene una duración limitada.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">
                  <HugeiconsIcon
                    icon={ArrowLeft01Icon}
                    data-icon="inline-start"
                  />
                  Volver a iniciar sesión
                </Link>
              </Button>
            </CardContent>
          </>
        ) : (
          <>
            <CardHeader>
              <div className="mb-2 flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <HugeiconsIcon icon={Mail01Icon} className="size-6" />
              </div>
              <CardTitle variant="page">
                <h1>Recupera tu acceso.</h1>
              </CardTitle>
              <CardDescription>
                Ingresa tu correo y te enviaremos un enlace para restablecer tu
                contraseña.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="flex flex-col gap-6"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    rules={{
                      required: 'El correo es obligatorio',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Ingresa un correo válido',
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Correo electrónico</FormLabel>
                        <InputGroup>
                          <InputGroupAddon align="inline-start">
                            <HugeiconsIcon icon={Mail01Icon} />
                          </InputGroupAddon>
                          <FormControl>
                            <InputGroupInput
                              type="email"
                              autoComplete="email"
                              spellCheck={false}
                              autoCapitalize="none"
                              placeholder="tucorreo@stgeorgeenglish.com"
                              {...field}
                            />
                          </FormControl>
                        </InputGroup>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    size="lg"
                    disabled={form.formState.isSubmitting}
                    className="w-full"
                  >
                    {form.formState.isSubmitting
                      ? 'Enviando…'
                      : 'Enviar enlace'}
                  </Button>

                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="self-center"
                  >
                    <Link to="/login">
                      <HugeiconsIcon
                        icon={ArrowLeft01Icon}
                        data-icon="inline-start"
                      />
                      Volver a iniciar sesión
                    </Link>
                  </Button>
                </form>
              </Form>
            </CardContent>
          </>
        )}
      </Card>
    </AuthShell>
  )
}
